import { useI18n } from '@/i18n'
import { Plus, X } from '@/shared/icons/phosphor'
import {
  type Component,
  For,
  type JSX,
  Show,
  children,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  on,
} from 'solid-js'
import { Input } from '../Input'
import { ReadOnlyValue } from '../ReadOnlyValue'

export interface ColorSwatchPickerProps {
  value: string | null
  onChange: (hex: string | null) => void
  presets: readonly string[]
  label: string
  noneLabel: string
  customLabel: string
  disabled?: boolean | undefined
  readOnly?: boolean | undefined
  hint?: JSX.Element | undefined
}

type SwatchOption = { kind: 'none' } | { kind: 'preset'; hex: string } | { kind: 'custom' }

const HEX_COLOR = /^#[0-9a-f]{6}$/

const normalizeHex = (input: string): string | null => {
  const lowered = input.trim().toLowerCase()
  const prefixed = lowered.startsWith('#') ? lowered : `#${lowered}`
  return HEX_COLOR.test(prefixed) ? prefixed : null
}

export const ColorSwatchPicker: Component<ColorSwatchPickerProps> = (props) => {
  const { t } = useI18n()
  const uid = createUniqueId()
  const labelId = `color-swatch-picker-label-${uid}`
  const hintId = `color-swatch-picker-hint-${uid}`
  const hint = children(() => props.hint)

  const presets = createMemo(() => props.presets.map((preset) => preset.toLowerCase()))
  const options = createMemo<SwatchOption[]>(() => [
    { kind: 'none' },
    ...presets().map((hex): SwatchOption => ({ kind: 'preset', hex })),
    { kind: 'custom' },
  ])
  const current = () => props.value?.toLowerCase() ?? null
  const isCustomValue = () => {
    const value = current()
    return value !== null && !presets().includes(value)
  }
  const checkedIndex = createMemo(() => {
    const value = current()
    if (value === null) {
      return 0
    }
    const presetIndex = presets().indexOf(value)
    return presetIndex >= 0 ? presetIndex + 1 : presets().length + 1
  })

  const [focusedIndex, setFocusedIndex] = createSignal<number | null>(null)
  const tabStop = () => focusedIndex() ?? checkedIndex()

  const [customOpen, setCustomOpen] = createSignal(false)
  const showCustom = () => customOpen() || isCustomValue()
  const [draft, setDraft] = createSignal(props.value ?? '')
  const [touched, setTouched] = createSignal(false)

  createEffect(
    on(
      () => props.value,
      (value) => {
        setDraft(value ?? '')
        setTouched(false)
      },
      { defer: true }
    )
  )

  const draftError = () => {
    const text = draft().trim()
    if (!touched() || text === '' || normalizeHex(text) !== null) {
      return ''
    }
    return t('designSystem.colorSwatchPicker.invalidHex')
  }

  const buttons: HTMLButtonElement[] = []
  let hexInput: HTMLInputElement | undefined

  const commit = (text: string) => {
    if (props.disabled) {
      return
    }
    const hex = normalizeHex(text)
    if (hex !== null) {
      props.onChange(hex)
    }
  }

  const select = (index: number) => {
    const option = options()[index]
    if (props.disabled || !option) {
      return
    }
    if (option.kind === 'custom') {
      setCustomOpen(true)
      queueMicrotask(() => hexInput?.focus())
      return
    }
    setCustomOpen(false)
    props.onChange(option.kind === 'preset' ? option.hex : null)
  }

  const focusOption = (index: number) => {
    const count = options().length
    buttons[(index + count) % count]?.focus()
  }

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        focusOption(index + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        focusOption(index - 1)
        break
      case 'Home':
        event.preventDefault()
        focusOption(0)
        break
      case 'End':
        event.preventDefault()
        focusOption(options().length - 1)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        select(index)
        break
    }
  }

  const optionLabel = (option: SwatchOption) => {
    if (option.kind === 'none') {
      return props.noneLabel
    }
    return option.kind === 'preset' ? option.hex.toUpperCase() : props.customLabel
  }

  const fill = (option: SwatchOption): string | null => {
    if (option.kind === 'preset') {
      return option.hex
    }
    return option.kind === 'custom' && isCustomValue() ? current() : null
  }

  const swatchStyle = (option: SwatchOption): JSX.CSSProperties | undefined => {
    const color = fill(option)
    return color ? { 'background-color': color } : undefined
  }

  const swatchClass = (option: SwatchOption, checked: boolean) =>
    [
      'flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-12)] border border-[var(--color-border)]',
      'transition-transform duration-[var(--duration-normal)] motion-reduce:transition-none',
      'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-primary-500)]',
      'disabled:cursor-default',
      fill(option)
        ? ''
        : 'bg-[var(--color-surface)] text-[color:var(--color-text-muted)] disabled:text-[color:var(--color-text-subtle)]',
      checked
        ? 'ring-2 ring-[var(--color-text)] ring-offset-2 ring-offset-[var(--color-surface)]'
        : 'enabled:hover:scale-105',
    ]
      .filter(Boolean)
      .join(' ')

  const readOnlyValue = (): JSX.Element => (
    <ReadOnlyValue label={props.label}>
      <div class="flex items-center gap-3">
        <Show
          when={current()}
          fallback={
            <span class="text-[length:var(--text-base)] text-[color:var(--color-text)]">
              {props.noneLabel}
            </span>
          }
        >
          {(hex) => [
            <span
              data-slot="swatch"
              aria-hidden="true"
              class="h-8 w-8 shrink-0 rounded-[var(--radius-8)] border border-[var(--color-border)]"
              style={{ 'background-color': hex() }}
            />,
            <span class="text-[length:var(--text-base)] tabular-nums text-[color:var(--color-text)]">
              {hex().toUpperCase()}
            </span>,
          ]}
        </Show>
      </div>
    </ReadOnlyValue>
  )

  return (
    <Show when={!props.readOnly} fallback={readOnlyValue()}>
      <div>
        <div
          id={labelId}
          class="mb-2 text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]"
        >
          {props.label}
        </div>
        <div
          role="radiogroup"
          aria-labelledby={labelId}
          aria-describedby={hint() ? hintId : undefined}
          aria-disabled={props.disabled ? true : undefined}
          class="flex flex-wrap gap-3"
        >
          <For each={options()}>
            {(option, index) => (
              <button
                ref={(element) => {
                  buttons[index()] = element
                }}
                type="button"
                role="radio"
                aria-checked={checkedIndex() === index()}
                aria-label={optionLabel(option)}
                title={optionLabel(option)}
                tabIndex={tabStop() === index() ? 0 : -1}
                disabled={props.disabled}
                onClick={() => select(index())}
                onKeyDown={(event) => onKeyDown(event, index())}
                onFocus={() => setFocusedIndex(index())}
                onBlur={() => setFocusedIndex(null)}
                class={swatchClass(option, checkedIndex() === index())}
                style={swatchStyle(option)}
              >
                <Show when={option.kind === 'none'}>
                  <X class="h-4 w-4" aria-hidden="true" />
                </Show>
                <Show when={option.kind === 'custom' && !isCustomValue()}>
                  <Plus class="h-4 w-4" aria-hidden="true" />
                </Show>
              </button>
            )}
          </For>
        </div>
        <Show when={showCustom()}>
          <div class="mt-3 flex items-start gap-3">
            <input
              type="color"
              aria-label={t('designSystem.colorSwatchPicker.picker')}
              value={current() ?? '#000000'}
              disabled={props.disabled}
              onInput={(event) => commit(event.currentTarget.value)}
              class="h-10 w-12 shrink-0 cursor-pointer rounded-[var(--radius-12)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1 disabled:cursor-default"
            />
            <div class="min-w-0 flex-1">
              <Input
                ref={(element) => {
                  hexInput = element
                }}
                aria-label={t('designSystem.colorSwatchPicker.hex')}
                value={draft()}
                placeholder="#000000"
                maxLength={7}
                autocomplete="off"
                spellcheck={false}
                readOnly={props.disabled}
                error={draftError()}
                onInput={(event) => {
                  setDraft(event.currentTarget.value)
                  commit(event.currentTarget.value)
                }}
                onBlur={() => setTouched(true)}
              />
            </div>
          </div>
        </Show>
        <Show when={hint()}>
          <div id={hintId} class="mt-2">
            {hint()}
          </div>
        </Show>
      </div>
    </Show>
  )
}
